
import { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { createToolbarEvent, sendAnalytics } from '../../../analytics';
import { openDialog, toggleDialog } from '../../../base/dialog';
import { MHRecordingConstants } from '../../../base/lib-meet-hour';
import { setVoiceCommandString } from '../../../base/mtApi/actions';
import { participantUpdated } from '../../../base/participants/actions';
import { getLocalParticipant } from '../../../base/participants/functions';
import { toggleScreensharing } from '../../../base/tracks/mhActions.web';
import { setIsPollsTabFocused } from '../../../chat/actions.any';
import { closeChat, openChat } from '../../../chat/actions.web';
import { clickPledgeDialog, updateClickAndPledgeSession } from '../../../clickandpledge-givent';
import { pollsTabFocusedHandler } from '../../../conference/actions.web';
import { addDonorurl, donorboxDialog } from '../../../donorbox';
import { toggleDocumentedit, toogleEtherPad } from '../../../etherpad';
import { beginAddPeople } from '../../../invite';
import {
    close as closeParticipantsPane,
    open as openParticipantsPane
} from '../../../participants-pane/actions';
import {
    StartLiveStreamDialog,
    StartRecordingDialog,
    StopLiveStreamDialog,
    StopRecordingDialog,
    getActiveSession
} from '../../../recording';
import { SETTINGS_TABS } from '../../../settings';
import { openSettingsDialog } from '../../../settings/actions';
import { showSharedVideoDialog } from '../../../shared-video/actions';
import { SpeakerStats } from '../../../speaker-stats';
import { VideoQualityDialog } from '../../../video-quality';
import { VirtualBackgroundDialog } from '../../../virtual-background/components/web';
import { sendWhiteBoardInstance, setFullScreen, setShareMenuVisible } from '../../actions.web';

/* global APP */

const selectMeetingState = state => {
    const jwt = state['features/base/jwt'].confSettings || {};
    const localParticipant = getLocalParticipant(state);
    const { getUrl, addUrl } = state['features/donorbox'];

    return {
        // conference / participant
        conference: state['features/base/conference'].conference,
        _getUrl: getUrl,
        _addUrl: addUrl,
        localParticipantID: localParticipant?.id,
        raisedHand: localParticipant?.raisedHand || false,

        // UI toggles
        whiteBoardVisible: jwt.WHITE_BOARD === 1,
        livePadVisible: jwt.LIVEPAD === 1,
        iframeVisible: state['features/toolbox'].iframeVisible,
        fullScreen: state['features/toolbox'].fullScreen,
        chatOpen: state['features/chat'].isOpen,
        isPollsTabFocused: state['features/chat'].isPollsTabFocused,
        participantsPaneOpen: state['features/participants-pane'].isOpen,

        // recordings / streaming
        isLiveStreamRunning: Boolean(getActiveSession(state, MHRecordingConstants.mode.STREAM)),
        isRecordingRunning: Boolean(getActiveSession(state, MHRecordingConstants.mode.FILE)),

        // features
        vcString: state['features/base/mtApi'].voiceCommandString,
        getUrl: state['features/donorbox'].getUrl,
        clickAndPledgeUrl: state['features/clickandpledge'].clickAndPledgeUrl,
        shareEtherpad: state['features/etherpad'].editing,
        onEdit: state['features/etherpad'].onEdit,
        shareVideo: (() => {
            const s = state['features/shared-video'].status;

            return [ 'playing', 'pause', 'start' ].includes(s);
        })(),
        selectedVideoInputId: state['features/base/settings'].cameraDeviceId
    };
};

/**
 * Component handling various actions based on vcString value.
 *
 * @returns {React$Element<any>}
 */
const VCStringActionHandler = () => {
    const [ prevVcString, setPrevVcString ] = useState(null);

    // Use selectors to get state data instead of props
    const {
        conference: _conference,

        // localParticipant,
        // _addUrl,
        _getUrl,
        localParticipantID,
        raisedHand = false,
        whiteBoardVisible: whiteBoardvisible,
        livePadVisible,
        iframeVisible,
        fullScreen: _fullScreen,
        chatOpen,
        isPollsTabFocused,
        participantsPaneOpen,
        isLiveStreamRunning,
        isRecordingRunning,
        vcString,
        getUrl,
        clickAndPledgeUrl,
        shareEtherpad,
        onEdit,
        shareVideoState,
        selectedVideoInputId
    } = useSelector(selectMeetingState, shallowEqual);


    const dispatch = useDispatch();

    /**
     * Toggle chat open/closed
     *
     * @returns {void}
     */
    const onToolbarToggleChat = useCallback(() => {

        // Send analytics event
        sendAnalytics(
            createToolbarEvent('toggle.chat', {
                enable: !chatOpen || isPollsTabFocused
            })
        );

        if (chatOpen) {
            // Chat is open
            if (isPollsTabFocused) {
                // Polls tab is focused, switch to chat tab
                dispatch(pollsTabFocusedHandler(false));
                dispatch(setIsPollsTabFocused(false));
            } else {
                // Chat tab is already focused, so close the chat
                dispatch(closeChat());
            }
        } else {
            // Chat is closed, open it and set chat tab focused
            dispatch(openChat());
            dispatch(pollsTabFocusedHandler(false));
            dispatch(setIsPollsTabFocused(false));
        }
    }, [ dispatch, chatOpen, isPollsTabFocused, participantsPaneOpen ]);

    /**
     * Toggle participants pane open/closed
     *
     * @returns {void}
     */
    const onToolbarToggleParticipantsPane = useCallback(() => {

        if (participantsPaneOpen) {
            dispatch(closeParticipantsPane());
        } else {
            dispatch(openParticipantsPane());
        }

        // dispatch(setParticipantsPaneOpen(!participantsPaneOpen));
    }, [ dispatch, participantsPaneOpen, chatOpen ]);

    /**
     * Open video quality dialog
     *
     * @returns {void}
     */
    const onToolbarOpenVideoQuality = useCallback(() => {
        dispatch(toggleDialog(VideoQualityDialog));
    }, [ dispatch ]);

    /**
     * Open speaker stats dialog
     *
     * @returns {void}
     */
    const onToolbarOpenSpeakerStats = useCallback(() => {
        dispatch(openDialog(SpeakerStats, {
            conference: _conference
        }));
    }, [ dispatch ]);

    /**
     * Toggle fullscreen
     *
     * @returns {void}
     */
    const onToolbarToggleFullScreen = useCallback(() => {
        const fullScreen = !_fullScreen;

        dispatch(setFullScreen(fullScreen));

        // dispatch(toggleFullScreen());
    }, [ dispatch ]);

    /**
     * Open invite dialog
     *
     * @returns {void}
     */
    const onToolbarOpenInvite = useCallback(() => {
        dispatch(beginAddPeople());
    }, [ dispatch ]);

    /**
     * Toggle raise hand status
     *
     * @returns {void}
     */
    const doToggleRaiseHand = useCallback(() => {
        dispatch(
            participantUpdated({
                // Only the local participant is allowed to update without
                // stating the MHConference instance because the local
                // participant is uniquely identified by being local
                id: localParticipantID,
                local: true,
                raisedHand: !raisedHand
            })
        );
    }, [ dispatch, localParticipantID, raisedHand ]);

    /**
     * Toggle donorbox dialog
     *
     * @returns {void}
     */
    const doToggleDonorboxDialog = useCallback(() => {
        dispatch(toggleDialog(donorboxDialog));
    }, [ dispatch ]);

    /**
     * Remove donor dialog
     *
     * @returns {void}
     */
    const doRemoveDonorDialog = useCallback(() => {
        dispatch(addDonorurl('remove'));
    }, [ dispatch ]);

    /**
     * Toggle screenshare
     *
     * @param {boolean} enabled - Flag indicating if we should enable screen sharing.
     * @returns {void}
     */
    const doToggleScreenshare = useCallback(enabled => {
        dispatch(toggleScreensharing(enabled));
    }, [ dispatch ]);

    /**
     * Toggle CLP dialog
     *
     * @returns {void}
     */
    const doToggleCLPDialog = useCallback(() => {
        dispatch(toggleDialog(clickPledgeDialog));
    }, [ dispatch ]);

    /**
     * Remove CLP dialog
     *
     * @returns {void}
     */
    const doRemoveClpDialog = useCallback(() => {
        dispatch(updateClickAndPledgeSession('remove'));
    }, [ dispatch ]);


    /**
     * Handles actions based on vcString value.
     *
     * @returns {void}
     */
    const handleVCStringAction = useCallback(() => {
        switch (vcString) {
        case 'DonorBox':
        case 'RDonorBox':
            if (_getUrl === undefined) {
                doToggleDonorboxDialog();
            } else {
                doRemoveDonorDialog();
            }
            break;

        case 'ScreenShare':
        case 'StopScreen':
            doToggleScreenshare(true);
            break;

        case 'Livestream':
            dispatch(
                    openDialog(
                        isLiveStreamRunning
                            ? StopLiveStreamDialog
                            : StartLiveStreamDialog
                    )
            );
            break;

        case 'WhiteBoard':
        case 'CWhiteBoard':
            if (whiteBoardvisible) {
                dispatch(sendWhiteBoardInstance(!iframeVisible));
                dispatch(setShareMenuVisible(false));
            }
            break;

        case 'LivePad':
        case 'CLivePad':
            if (livePadVisible) {
                dispatch(toogleEtherPad(!shareEtherpad));
                dispatch(toggleDocumentedit(!onEdit));
            }
            break;

        case 'Youtube':
        case 'CYoutube':
            if (shareVideoState === false) {
                dispatch(
                        showSharedVideoDialog(id => APP.UI.startSharedVideoEmitter(id))
                );
            } else {
                APP.UI.stopSharedVideoEmitter();
            }
            break;

        case 'clicknPledge':
        case 'RclicknPledge':
            if (clickAndPledgeUrl === undefined) {
                doToggleCLPDialog();
            } else {
                doRemoveClpDialog();
            }
            break;

        case 'Recording':
            dispatch(
                    openDialog(
                        isRecordingRunning
                            ? StopRecordingDialog
                            : StartRecordingDialog
                    )
            );
            break;

        case 'ParticipantP':
            onToolbarToggleParticipantsPane();
            break;

        case 'VcDialog':
            dispatch(openDialog(VirtualBackgroundDialog, {
                selectedVideoInputId
            }));
            break;

        case 'ChatBox':
            onToolbarToggleChat();
            break;

        case 'VideoQ':
            onToolbarOpenVideoQuality();
            break;

        case 'SpeckerS':
            onToolbarOpenSpeakerStats();
            break;

        case 'Settings':
        {

            const defaultTab = SETTINGS_TABS.DEVICES;

            dispatch(openSettingsDialog(defaultTab));
            break;
        }

        case 'Fullscreen':
            onToolbarToggleFullScreen();
            break;

        case 'InviteP':
            onToolbarOpenInvite();
            break;

        case 'RaiseHand':
        case 'LowerHand':
            doToggleRaiseHand();
            break;
        }
        dispatch(setVoiceCommandString(null));
    }, [
        vcString, getUrl, doToggleDonorboxDialog, doRemoveDonorDialog,
        doToggleScreenshare, dispatch, isLiveStreamRunning, whiteBoardvisible,
        iframeVisible, livePadVisible, shareEtherpad, onEdit, shareVideoState,
        clickAndPledgeUrl, doToggleCLPDialog, doRemoveClpDialog, isRecordingRunning,
        onToolbarToggleParticipantsPane, selectedVideoInputId, onToolbarToggleChat,
        onToolbarOpenVideoQuality, onToolbarOpenSpeakerStats, onToolbarToggleFullScreen,
        onToolbarOpenInvite, doToggleRaiseHand
    ]);

    useEffect(() => {
        if (prevVcString !== vcString && vcString !== null) {
            handleVCStringAction();
            setPrevVcString(vcString);
        }
    }, [ vcString, prevVcString ]);

    return null;
};

export default VCStringActionHandler;
